import { createFileRoute, Link } from "@tanstack/react-router";
import {
  BookOpenCheck,
  Quote,
  Sparkles,
  GraduationCap,
  Check,
  Pencil,
  FileSearch,
  ListOrdered,
  Lightbulb,
  Mic2,
  Save,
  Lock,
  MessagesSquare,
  ClipboardList,
  Languages,
  Users,
  ShieldCheck,
  ArrowRight,
  Brain,
  Target,
  Layers,
  Feather,
  Scale,
  Search,
  BadgeCheck,
  Type as TypeIcon,
  Repeat,
  Home,
  School,
  User,
  BookOpen,
  Heart,
  PenLine,
  MessageSquareQuote,
  Trophy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { AcademicDisclaimer } from "@/components/AcademicDisclaimer";
import { SiteHeader } from "@/components/SiteHeader";


const HOME_TITLE = "SmartEssay — AI Writing Coach for Critical Thinking | Teacher-Created";
const HOME_DESC = "SmartEssay is the teacher-created AI writing coach that helps students brainstorm, outline, draft, revise, cite sources, and strengthen critical thinking — all in one platform.";
const HOME_OG_IMAGE = "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/e7b01c90-1efc-4c70-b262-5ef3ec65fec7/id-preview-7157284e--9ac35d9b-7a1d-447e-a99b-cca27bc4536b.lovable.app-1779981472342.png";

// ---------- Content data ----------

const COACH_TOOLS: {
  icon: typeof Brain;
  title: string;
  body: string;
  benefit: string;
  to: string;
  cta: string;
}[] = [
  { icon: Brain, title: "Essay Brainstorming", body: "Generate angles, questions, and hooks before you start writing.", benefit: "Beat the blank page.", to: "/tutor", cta: "Start brainstorming" },
  { icon: ListOrdered, title: "Essay Outliner", body: "Framed outlines with sentence starters that guide your structure.", benefit: "Organize thinking, not just words.", to: "/generator", cta: "Build an outline" },
  { icon: Target, title: "Thesis Builder", body: "Sharpen a vague argument into a defensible, specific thesis.", benefit: "A stronger claim, in minutes.", to: "/tutor", cta: "Refine your thesis" },
  { icon: Feather, title: "Introduction Generator", body: "Hook + context + thesis scaffolds tuned to your prompt.", benefit: "Open with purpose.", to: "/tutor", cta: "Draft an intro" },
  { icon: Layers, title: "Body Paragraph Assistant", body: "Claim → evidence → analysis coaching for every paragraph.", benefit: "Argue like a scholar.", to: "/tutor", cta: "Open the assistant" },
  { icon: Scale, title: "Counterclaim Coach", body: "Surface the strongest opposing view and coach a fair response.", benefit: "Show real critical thinking.", to: "/tutor", cta: "Handle counterclaims" },
  { icon: Search, title: "Evidence Analyzer", body: "Find weak spots where a claim needs a real quote or data.", benefit: "No more unsupported claims.", to: "/reviewer", cta: "Analyze evidence" },
  { icon: Quote, title: "Citation Generator", body: "Real MLA & APA 7 citations, word-for-word — never hallucinated.", benefit: "Cite it right the first time.", to: "/essay-writer", cta: "Generate citations" },
  { icon: BookOpenCheck, title: "Grammar Checker", body: "Grammar, clarity, and style tuned for academic writing.", benefit: "Polish without dumbing down.", to: "/essay-checker", cta: "Check my grammar" },
  { icon: Pencil, title: "Revision Assistant", body: "Paragraph-by-paragraph feedback with prioritized fixes.", benefit: "Know exactly what to change.", to: "/reviewer", cta: "Revise my draft" },
  { icon: BadgeCheck, title: "Essay Scoring", body: "Rubric-style feedback across thesis, evidence, structure, and style.", benefit: "See your grade before your teacher does.", to: "/reviewer", cta: "Score my essay" },
  { icon: ShieldCheck, title: "AI Detection", body: "Flag AI-sounding sentences so students rewrite in their own voice.", benefit: "Own your writing.", to: "/features", cta: "Check for AI" },
  { icon: FileSearch, title: "Plagiarism Checker", body: "Cross-check phrasing against public sources before you submit.", benefit: "Submit with confidence.", to: "/features", cta: "Check originality" },
  { icon: Repeat, title: "Paraphrasing Tool", body: "Rewrite a passage clearly — with the option to keep a real citation.", benefit: "Understand it, then say it your way.", to: "/features", cta: "Paraphrase text" },
  { icon: BookOpen, title: "Summarizer", body: "Turn a long article or chapter into study-ready key points.", benefit: "Read faster, retain more.", to: "/generator", cta: "Summarize a source" },
  { icon: Languages, title: "Translator", body: "Move between languages while keeping academic meaning intact.", benefit: "Write in the language of the class.", to: "/tutor", cta: "Translate a passage" },
  { icon: TypeIcon, title: "Formatting Assistant", body: "MLA & APA 7 headers, spacing, Works Cited — done automatically.", benefit: "Format the paper, focus on the ideas.", to: "/essay-writer", cta: "Format my paper" },
];

const LEARNING_STEPS: { step: string; title: string; body: string }[] = [
  { step: "1", title: "Brainstorm", body: "Explore angles, ask better questions, and land on a topic that's actually interesting to defend." },
  { step: "2", title: "Organize", body: "Turn ideas into a framed outline with a working thesis and clear paragraph roles." },
  { step: "3", title: "Draft", body: "Write section-by-section with sentence frames, live word counts, and source-aware prompts." },
  { step: "4", title: "Revise", body: "Get evidence gaps, a paragraph-by-paragraph flow map, and prioritized fixes." },
  { step: "5", title: "Improve", body: "Tighten grammar, clarity, and voice — and check for AI-sounding sentences." },
  { step: "6", title: "Submit", body: "Export a properly formatted MLA or APA 7 PDF with citations and Works Cited." },
];


const AUDIENCES: { icon: typeof User; title: string; body: string }[] = [
  { icon: User,          title: "Students",           body: "A patient coach for every stage of the essay — not a shortcut." },
  { icon: GraduationCap, title: "Teachers",           body: "Assign SmartEssay for scaffolded practice that reinforces the writing process." },
  { icon: School,        title: "Schools",            body: "One integrity-first writing platform for whole-class or district-wide use." },
  { icon: Home,          title: "Homeschool Families",body: "A structured writing tutor at home — with a parent lock for peace of mind." },
  { icon: BookOpen,      title: "College Students",   body: "Faster research, stronger arguments, clean MLA & APA citations." },
  { icon: Heart,         title: "Adult Learners",     body: "Return to writing with a coach that meets you where you are." },
];

const FAQS: { q: string; a: string }[] = [
  { q: "How is SmartEssay different from ChatGPT?", a: "ChatGPT is a general chatbot that will happily write an essay for you. SmartEssay is a teacher-created writing coach: it walks students through brainstorming, outlining, drafting, revising, and citing — so they learn the writing process instead of skipping it." },
  { q: "Does SmartEssay write essays for students?", a: "SmartEssay can generate sample essays for study and reference, but the whole product is designed to keep students in the driver's seat. Modes like the Guided Tutor and Parent Lock deliberately slow the process down and coach the student paragraph-by-paragraph." },
  { q: "How does SmartEssay improve student writing?", a: "By breaking essays into teachable steps — thesis, evidence, counterclaim, revision — and giving targeted feedback at each step. Students practice the moves that separate a C paper from an A paper: specific claims, real evidence, clear structure, honest counterclaims, and careful revision." },
  { q: "Can teachers use SmartEssay in class?", a: "Yes. Teachers use SmartEssay for scaffolded assignments, in-class outlining, revision workshops, and rubric-style feedback. It reinforces the writing process you already teach." },
  { q: "Is SmartEssay suitable for middle school?", a: "Yes. The tutor, outliner, and revision assistant are age-appropriate, and the optional Parent Lock restricts the account to coaching mode so younger students focus on their own writing." },
  { q: "Can colleges and universities use SmartEssay?", a: "Yes. College students use SmartEssay for research papers, argumentative essays, and literature analysis — with real MLA and APA 7 citations, source-aware drafting, and evidence review." },
  { q: "Does SmartEssay support MLA format?", a: "Yes. SmartEssay generates proper MLA in-text citations and a Works Cited page, and exports formatted MLA PDFs with correct heading and spacing." },
  { q: "Does SmartEssay support APA format?", a: "Yes. SmartEssay follows APA 7 for in-text citations and References, including italicized titles and journal names, and exports formatted APA PDFs." },
  { q: "Can SmartEssay detect plagiarism?", a: "SmartEssay includes originality checks that compare phrasing against public sources so students catch unintentional overlap before submission. It's a coaching tool, not a substitute for a school's official integrity software." },
  { q: "How does AI detection work in SmartEssay?", a: "SmartEssay analyzes sentence patterns, vocabulary, and rhythm that tend to appear in AI-generated text, then flags passages so students can rewrite them in their own voice before turning the essay in." },
];

const LEARN_LINKS: string[] = [
  "How to Write a Thesis Statement",
  "How to Write an Argumentative Essay",
  "MLA Citation Guide",
  "APA Citation Guide",
  "How to Avoid Plagiarism",
  "Essay Revision Checklist",
  "Writing Strong Conclusions",
  "Using Evidence Effectively",
  "Critical Thinking Skills for Students",
  "Essay Writing Tips",
];

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: HOME_TITLE },
      { name: "description", content: HOME_DESC },
      { property: "og:title", content: HOME_TITLE },
      { property: "og:description", content: HOME_DESC },
      { property: "og:url", content: "https://smartessay.ai/" },
      { property: "og:image", content: HOME_OG_IMAGE },
      { name: "twitter:image", content: HOME_OG_IMAGE },
      { name: "twitter:title", content: HOME_TITLE },
      { name: "twitter:description", content: HOME_DESC },
    ],
    links: [
      { rel: "canonical", href: "https://smartessay.ai/" },
      { rel: "preload", as: "image", href: "/images/hero-library.webp", fetchpriority: "high", type: "image/webp" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              name: "SmartEssay",
              url: "https://smartessay.ai",
              logo: "https://smartessay.ai/icon-512.png",
              description: HOME_DESC,
              founder: { "@type": "Person", name: "Nicole" },
              sameAs: [],
            },
            {
              "@type": "WebSite",
              name: "SmartEssay",
              url: "https://smartessay.ai",
              potentialAction: {
                "@type": "SearchAction",
                target: "https://smartessay.ai/app?title={search_term_string}",
                "query-input": "required name=search_term_string",
              },
            },
            {
              "@type": "SoftwareApplication",
              name: "SmartEssay",
              applicationCategory: "EducationalApplication",
              applicationSubCategory: "AI Writing Coach",
              operatingSystem: "Web, iOS, Android",
              offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
              description: HOME_DESC,
              featureList: COACH_TOOLS.map((t) => t.title),
              aggregateRating: {
                "@type": "AggregateRating",
                ratingValue: "3.75",
                reviewCount: "2",
              },
            },
            {
              "@type": "HowTo",
              name: "How to write an essay with SmartEssay",
              description: "SmartEssay coaches students through six steps of the writing process.",
              step: LEARNING_STEPS.map((s) => ({
                "@type": "HowToStep",
                position: Number(s.step),
                name: s.title,
                text: s.body,
              })),
            },
            {
              "@type": "FAQPage",
              mainEntity: FAQS.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ],
        }),
      },
    ],
  }),
  component: Landing,
});

// ---------- Component ----------

function Landing() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />

      <main className="mx-auto max-w-6xl px-6 pb-24 pt-12 sm:pt-16">

        {/* ================= HERO ================= */}
        <section className="grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-center lg:gap-14">
          <div className="text-left">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-border bg-muted/60 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              A Tilt.University product
            </div>
            <h1 className="text-4xl font-bold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              A writing coach that teaches students to think — not just submit.
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              SmartEssay walks students through the writing process teachers already teach:
              brainstorm, organize, draft, revise, cite, and reflect. Built by a teacher, inside a
              real classroom.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/auth">
                <Button size="lg" className="rounded-full bg-primary text-base font-medium text-primary-foreground hover:bg-primary/90 px-7 h-12">
                  Start free
                </Button>
              </Link>
              <a href="#how-it-works">
                <Button size="lg" variant="outline" className="rounded-full text-base font-medium px-6 h-12">
                  See how it works
                </Button>
              </a>
            </div>

            <div className="mt-6">
              <AcademicDisclaimer variant="badge" />
            </div>
          </div>


          {/* Hero visual — the Writing Journey */}
          <div className="relative">
            <div className="absolute -left-2 -top-2 h-8 w-8 border-l-2 border-t-2 border-gold" aria-hidden />
            <div className="absolute -bottom-2 -right-2 h-8 w-8 border-b-2 border-r-2 border-gold" aria-hidden />
            <div className="relative overflow-hidden rounded-2xl border border-primary/15 bg-white shadow-deep">
              {/* Card header */}
              <div className="flex items-center justify-between border-b border-border/70 bg-gradient-to-r from-[#F8FAFC] to-white px-5 py-3">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-[#FCA5A5]" aria-hidden />
                  <span className="h-2.5 w-2.5 rounded-full bg-[#FCD34D]" aria-hidden />
                  <span className="h-2.5 w-2.5 rounded-full bg-[#86EFAC]" aria-hidden />
                </div>
                <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
                  <Sparkles className="h-3.5 w-3.5" />
                  Writing Journey
                </div>
              </div>

              {/* Journey steps */}
              <div className="p-6">
                <ol className="space-y-3">
                  {[
                    { icon: Lightbulb,           label: "Idea",             hint: "Explore angles worth defending",       tone: "sky"    },
                    { icon: Search,              label: "Research",         hint: "Vet sources with real citations",       tone: "teal"   },
                    { icon: ListOrdered,         label: "Organization",     hint: "Framed outline, working thesis",        tone: "purple" },
                    { icon: PenLine,             label: "Draft",            hint: "Sentence-frame guidance, your voice",   tone: "blue"   },
                    { icon: MessageSquareQuote,  label: "Feedback",         hint: "Rubric coaching, prioritized fixes",    tone: "gold"   },
                    { icon: Trophy,              label: "Improved Essay",   hint: "Formatted MLA/APA, ready to submit",    tone: "navy"   },
                  ].map(({ icon: Icon, label, hint, tone }, i, arr) => {
                    const bg =
                      tone === "sky"    ? "bg-[#EFF6FF] text-[#2563EB]" :
                      tone === "teal"   ? "bg-[#ECFDF5] text-[#0F766E]" :
                      tone === "purple" ? "bg-[#F5F3FF] text-[#7C3AED]" :
                      tone === "blue"   ? "bg-[#DBEAFE] text-[#1D4ED8]" :
                      tone === "gold"   ? "bg-[#FEF3C7] text-[#B45309]" :
                                          "bg-[#0F172A] text-white";
                    return (
                      <li key={label} className="group relative flex items-center gap-4">
                        <div className="relative flex flex-col items-center">
                          <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${bg} shadow-sm ring-1 ring-black/[0.03]`}>
                            <Icon className="h-5 w-5" aria-hidden />
                          </span>
                          {i < arr.length - 1 && (
                            <span className="mt-1 h-4 w-px bg-gradient-to-b from-border to-transparent" aria-hidden />
                          )}
                        </div>
                        <div className="flex-1 rounded-xl border border-border/70 bg-white px-4 py-2.5 shadow-[0_1px_0_rgba(15,23,42,0.02)] transition-colors group-hover:border-primary/25">
                          <div className="flex items-center justify-between gap-3">
                            <span className="font-display text-sm font-semibold text-foreground">{label}</span>
                            <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                              Step {i + 1}
                            </span>
                          </div>
                          <p className="mt-0.5 text-xs leading-snug text-muted-foreground">{hint}</p>
                        </div>
                      </li>
                    );
                  })}
                </ol>

                {/* Footer summary */}
                <div className="mt-5 flex items-center justify-between gap-3 rounded-xl border border-primary/15 bg-[#EFF6FF]/60 px-4 py-3">
                  <div className="flex items-center gap-2 text-xs font-medium text-foreground/80">
                    <ShieldCheck className="h-4 w-4 text-primary" aria-hidden />
                    AI coaches the process — students own the writing.
                  </div>
                  <span className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold text-primary">
                    Learn more <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </div>
            </div>
            <p className="mt-4 text-right font-display text-xs italic tracking-wide text-muted-foreground">
              Scholarship, scaffolded.
            </p>
          </div>
        </section>


        {/* ================= WHY SMARTESSAY ================= */}
        <section className="mt-20 sm:mt-24">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-medium uppercase tracking-[0.22em] text-primary">Why SmartEssay?</p>
            <div className="mx-auto mt-3 h-px w-16 bg-gold/70" aria-hidden />
            <h2 className="mt-4 font-display text-4xl font-medium sm:text-5xl">
              An AI writing coach — not an AI essay generator.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-foreground/85">
              Most AI writing tools focus on producing essays. SmartEssay focuses on helping students
              develop stronger writing and critical thinking skills through guided support. It's built by
              a teacher, for the classroom, and it coaches every step of the writing process:
              brainstorming, outlining, thesis development, organization, revision, citation, grammar,
              critical thinking, and reflection.
            </p>
          </div>
          <div className="mx-auto mt-10 grid max-w-4xl gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: Brain, title: "Critical thinking first", body: "Coaches the moves that make writing intelligent." },
              { icon: GraduationCap, title: "Teacher-created", body: "Designed inside a real classroom, not a lab." },
              { icon: ShieldCheck, title: "Integrity-safe", body: "Optional parent lock keeps students writing." },
              { icon: Layers, title: "All-in-one", body: "Brainstorm to citations — one login, one flow." },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="rounded-2xl border border-border bg-card p-6 text-left transition-colors hover:border-primary">
                <Icon className="mb-3 h-5 w-5 text-primary" />
                <h3 className="font-display text-base font-medium">{title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ================= COACH TOOLS GRID ================= */}
        <section className="mt-20 sm:mt-24" id="toolkit">
          <div className="mb-10 text-center">
            <p className="text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">The toolkit</p>
            <h2 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">Coaching for every part of the essay.</h2>
            <p className="mx-auto mt-4 max-w-2xl text-base text-muted-foreground">
              From first idea to final citation — each tool teaches a specific move in the writing process.
            </p>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {COACH_TOOLS.map(({ icon: Icon, title, body, benefit, to, cta }) => (
              <Link
                key={title}
                to={to}
                className="group relative flex flex-col rounded-2xl border border-border bg-card p-8 border-t-4 border-t-primary transition-colors hover:border-primary hover:border-t-primary-deep"
              >
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="font-display text-lg font-semibold leading-snug">{title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{body}</p>
                <p className="mt-3 text-xs font-medium text-primary/90">{benefit}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary group-hover:underline">
                  {cta} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>

            ))}
          </div>
          <div className="mt-8 flex justify-center">
            <Link to="/features">
              <Button variant="outline" className="btn-outline-pop rounded-full">
                See full feature list <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </section>

        {/* ================= LEARNING PROCESS ================= */}
        <section className="mt-24" id="how-it-works">
          <div className="mb-10 text-center">
            <p className="text-xs font-medium uppercase tracking-[0.22em] text-primary">The learning process</p>
            <div className="mx-auto mt-3 h-px w-16 bg-gold/70" aria-hidden />
            <h2 className="mt-4 font-display text-4xl font-medium sm:text-5xl">
              SmartEssay coaches every step.
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base text-muted-foreground">
              Instead of generating a finished essay, SmartEssay walks students through the same six-step
              writing process teachers use in the classroom.
            </p>
          </div>
          <ol className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {LEARNING_STEPS.map(({ step, title, body }) => (
              <li key={step} className="relative rounded-2xl border border-border bg-card p-8 border-t-4 border-t-secondary-accent transition-colors hover:border-primary">
                <div className="flex items-baseline gap-3">
                  <span className="font-display text-3xl text-gold">{step}</span>
                  <h3 className="font-display text-lg font-medium">{title}</h3>
                </div>
                <p className="mt-2 text-sm text-muted-foreground">{body}</p>
              </li>
            ))}
          </ol>
        </section>




        {/* ================= WHO IT'S FOR ================= */}
        <section className="mt-24">
          <div className="mb-10 text-center">
            <p className="text-xs font-medium uppercase tracking-[0.22em] text-primary">Who it's for</p>
            <div className="mx-auto mt-3 h-px w-16 bg-gold/70" aria-hidden />
            <h2 className="mt-4 font-display text-4xl font-medium sm:text-5xl">Built for anyone learning to write.</h2>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {AUDIENCES.map(({ icon: Icon, title, body }) => (
              <div key={title} className="rounded-2xl border border-border bg-card p-8 border-t-4 border-t-primary transition-colors hover:border-primary">
                <Icon className="mb-3 h-5 w-5 text-primary" />
                <h3 className="font-display text-lg font-medium">{title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{body}</p>
              </div>
            ))}
          </div>
        </section>




        {/* ================= FAQ ================= */}
        <section className="mx-auto mt-24 max-w-3xl">
          <div className="mb-8 text-center">
            <p className="text-xs font-medium uppercase tracking-[0.22em] text-primary">FAQ</p>
            <div className="mx-auto mt-3 h-px w-16 bg-gold/70" aria-hidden />
            <h2 className="mt-4 font-display text-4xl font-medium sm:text-5xl">Common questions</h2>
          </div>
          <Accordion type="single" collapsible className="mt-4">
            {FAQS.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`}>
                <AccordionTrigger className="text-left font-display text-lg">{f.q}</AccordionTrigger>
                <AccordionContent>{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* ================= LEARN MORE (internal links) ================= */}
        <section className="mt-24">
          <div className="mb-8 text-center">
            <p className="text-xs font-medium uppercase tracking-[0.22em] text-primary">Learn more</p>
            <div className="mx-auto mt-3 h-px w-16 bg-gold/70" aria-hidden />
            <h2 className="mt-4 font-display text-3xl font-medium sm:text-4xl">Writing guides &amp; resources</h2>
            <p className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground">
              Free lessons and guides for students, teachers, and homeschool families.
            </p>
          </div>
          <ul className="mx-auto grid max-w-4xl gap-2 sm:grid-cols-2">
            {LEARN_LINKS.map((label) => (
              <li key={label}>
                <Link
                  to="/blog"
                  className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3 text-sm text-foreground transition-colors hover:border-primary hover:text-primary"
                >
                  <span>{label}</span>
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                </Link>
              </li>
            ))}
          </ul>
        </section>

        {/* ================= PRICING ================= */}
        <section className="mt-24">
          <div className="mb-8 text-center">
            <p className="text-xs font-medium uppercase tracking-[0.22em] text-primary">Pricing</p>
            <div className="mx-auto mt-3 h-px w-16 bg-gold/70" aria-hidden />
            <h2 className="mt-4 font-display text-4xl font-medium sm:text-5xl">Simple, student-friendly pricing</h2>
            <p className="mt-3 text-sm text-muted-foreground">Try it free. Upgrade whenever you're ready. Cancel anytime.</p>
          </div>
          <div className="mx-auto grid max-w-3xl gap-5 sm:grid-cols-2">
            <div className="relative flex flex-col rounded-2xl border border-border bg-card p-8 border-t-4 border-t-primary transition-colors hover:border-primary">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Monthly</p>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="font-display text-4xl font-semibold">$4.99</span>
                <span className="text-sm text-muted-foreground">/month</span>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">Cancel anytime</p>
              <ul className="mt-5 space-y-2 text-sm text-foreground/80">
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />Unlimited coaching across all 17 tools</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />Reviewer + Guided AI Tutor</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />MLA &amp; APA 7 PDFs, parent lock</li>
              </ul>
            </div>
            <div className="relative flex flex-col rounded-2xl border border-primary bg-card p-8 border-t-4 border-t-primary shadow-glow">

              <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-primary-foreground">Best value</div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Yearly</p>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="font-display text-4xl font-semibold">$49</span>
                <span className="text-sm text-muted-foreground">/year</span>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">Best value for the school year</p>
              <ul className="mt-5 space-y-2 text-sm text-foreground/80">
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />Everything in Monthly</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />Just $4.08/month, billed yearly</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />Best value for the school year</li>
              </ul>
            </div>
          </div>
          <div className="mt-6 text-center">
            <Link to="/pricing" className="text-sm font-medium text-primary underline-offset-4 hover:underline">
              See full pricing details <ArrowRight className="ml-0.5 inline h-3.5 w-3.5" />
            </Link>
          </div>
        </section>

        {/* ================= FINAL CTA ================= */}
        <section className="mt-24 overflow-hidden rounded-sm border border-primary/40 bg-prestige p-12 text-center shadow-deep sm:p-16">
          <p className="text-xs font-medium uppercase tracking-[0.22em] text-gold">Begin</p>
          <div className="mx-auto mt-3 h-px w-16 bg-gold/70" aria-hidden />
          <h2 className="mt-5 font-display text-4xl font-medium text-primary-foreground sm:text-5xl">
            Coach your best essay yet.
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-primary-foreground/80">
            Free to try. Teacher-created. Every step of the writing process — one login.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/auth">
              <Button size="lg" variant="secondary" className="rounded-full bg-gold px-8 h-12 text-base font-semibold text-foreground shadow-deep hover:bg-gold/90 hover:-translate-y-0.5 transition-transform">
                Start Writing Free <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/features">
              <Button size="lg" variant="outline" className="rounded-full h-12 text-base border-primary-foreground/40 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
                See all features
              </Button>
            </Link>
          </div>
        </section>

        <section className="mt-14">
          <AcademicDisclaimer variant="inline" />
        </section>
      </main>
    </div>
  );
}



